import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, useInView, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Motion";

const muted = { color: "var(--kh-text-muted)" };

export const Opportunity = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" data-testid="home-opportunity">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
      <Reveal className="lg:col-span-8">
        <div className="kh-eyebrow mb-6">For ambitious therapists</div>
        <h2 className="kh-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Learn the framework that commands <span style={{ color: "var(--kh-accent)" }}>£1,000 an hour</span> from elite clients and secures major corporate contracts.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-4">
        <p className="text-base sm:text-lg leading-relaxed" style={muted}>The 5 Protocol Framework is a structured, growth-focused method for trauma, taught one-to-one to qualified therapists in just two days.</p>
        <Link to="/training" className="kh-btn-primary mt-8" data-testid="opportunity-training-button">Explore the training <span className="kh-arrow" aria-hidden="true">→</span></Link>
      </Reveal>
    </div>
  </section>
);

const CountUp = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduce = useReducedMotion();
  const [v, setV] = useState(reduce ? to : 0);
  useEffect(() => {
    if (!inView || reduce) return undefined;
    const c = animate(0, to, { duration: 2.2, ease: [0.2, 0.8, 0.2, 1], onUpdate: setV });
    return () => c.stop();
  }, [inView, reduce, to]);
  return <span ref={ref}>{v.toFixed(1)}</span>;
};

export const Results = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" style={{ background: "var(--kh-footer)" }} data-testid="home-results">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <Reveal className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.22em] mb-6" style={{ fontFamily: "var(--kh-font-mono)", color: "#D4A373" }}>Documented results</div>
        <div className="leading-none" style={{ fontFamily: "var(--kh-font-heading)", color: "#F7F4EF", fontSize: "clamp(5.5rem, 18vw, 13rem)" }} data-testid="results-stat">
          <CountUp to={98.4} /><span style={{ color: "#D4A373" }}>%</span>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-5">
        <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "var(--kh-font-heading)", color: "#F7F4EF" }}>Documented reduction in trauma symptoms.</h2>
        <p className="mt-5 text-base leading-relaxed" style={{ color: "#CFC6BA" }}>Measured with the DSM-5 PCL-5 scale, the standard checklist used to assess post-traumatic stress symptoms.</p>
        <p className="mt-8 pt-6 text-xs leading-relaxed" style={{ color: "#B5AB9F", borderTop: "1px solid #3A332D" }} data-testid="results-source">
          Source: ongoing internal monitoring of Kheiron PTG clients, in preparation for a longer study by John Moore University Liverpool, England.
        </p>
      </Reveal>
    </div>
  </section>
);

const ROWS = [
  ["Framework", "Rigid NICE guideline pathway", "Five-phase protocol, adapted to the client"],
  ["Target outcome", "Return to baseline function", "Documented growth beyond baseline"],
  ["Timing", "After symptoms consolidate (weeks → years)", "Pre-emptive window + post-growth phase"],
  ["Session structure", "Protocolised, symptom-led", "5 sessions on average, meaning-led"],
  ["Measurement", "Symptom reduction (PCL-5)", "PCL-5 + the five PTGI growth domains"],
];

export const Versus = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" data-testid="home-versus">
    <div className="max-w-7xl mx-auto">
      <Reveal className="max-w-3xl">
        <div className="kh-mono mb-4">Beyond standard EMDR</div>
        <h2 className="kh-serif text-4xl lg:text-5xl leading-[1.05]">For therapists who want real results, not just a rigid protocol.</h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed" style={muted}>Standard EMDR, delivered within the rigid NICE guidelines, is built to manage symptoms. The 5 Protocol Framework is built for what comes after: growth.</p>
      </Reveal>
      <div className="mt-14" data-testid="versus-table">
        <div className="hidden md:grid grid-cols-12 gap-6 pb-4" style={{ borderBottom: "1px solid var(--kh-border)" }}>
          <div className="col-span-3 kh-mono">Dimension</div>
          <div className="col-span-4 kh-mono">Standard EMDR</div>
          <div className="col-span-5 kh-mono" style={{ color: "var(--kh-accent)" }}>5 Protocol Framework</div>
        </div>
        {ROWS.map(([d, a, b], i) => (
          <Reveal key={d} delay={i * 0.05}>
            <div className="kh-row grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 py-5" style={{ borderBottom: "1px solid var(--kh-border)" }}>
              <div className="md:col-span-3 kh-serif text-xl">{d}</div>
              <div className="md:col-span-4 text-sm" style={muted}><span className="md:hidden kh-mono mr-2">EMDR</span>{a}</div>
              <div className="md:col-span-5 text-sm flex gap-3"><span className="md:hidden kh-mono" style={{ color: "var(--kh-accent)" }}>5PF</span>{b}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Demand = () => (
  <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" style={{ background: "var(--kh-surface)" }} data-testid="home-demand">
    <div className="max-w-5xl mx-auto">
      <Reveal>
        <div className="kh-eyebrow mb-6">An urgent need</div>
        <h2 className="kh-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15]" data-testid="demand-statement">
          With a <span style={{ color: "var(--kh-accent)" }}>98.4% documented success rate</span> on the DSM-5 PCL-5 scale, client demand is rapidly outpacing available therapists. Join us and fill an urgent need in your community.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <p className="md:col-span-7 text-base sm:text-lg leading-relaxed" style={muted}>That is why we are opening the framework to qualified therapists. Two days of one-to-one training, 14 CPD hours, remote or in person, at your own pace.</p>
        <div className="md:col-span-5 flex flex-wrap gap-4 md:justify-end">
          <Link to="/apply" className="kh-btn-primary" data-testid="demand-apply-button">Apply to train <span className="kh-arrow" aria-hidden="true">→</span></Link>
          <Link to="/therapy" className="kh-link self-center" data-testid="demand-therapy-link">See the method</Link>
        </div>
      </Reveal>
    </div>
  </section>
);
